// CostsProcedures.jsx
import React from 'react';
import { Calculator, DollarSign, TrendingDown, BarChart3, Clock, Users, MessageCircle, AlertTriangle } from 'lucide-react';
import './CostsProcedures.css';

const CostsProcedures = ({ data, onChange, validationErrors = {} }) => {
  const handleMultiSelect = (field, value) => {
    const currentValues = data[field] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onChange(field, newValues);
  };

  const handleOtherFieldChange = (field, value) => {
    onChange(field, value);
  };

  // Helper function to get error class
  const getErrorClass = (fieldName) => {
    return validationErrors[fieldName] ? 'error' : '';
  };

  // Helper function to render error message
  const renderError = (fieldName) => {
    if (validationErrors[fieldName]) {
      return (
        <div className="error-message" role="alert">
          {validationErrors[fieldName]}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="step-content">
      <div className="step-header-section">
        {/* <Calculator className="section-icon" size={32} /> */}
        <h2>سابعاً: التكاليف والإجراءات المرتبطة بعملية الإفصاح</h2>
      </div>
      
      {/* Financial Costs Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">20</span>
          <label className="question-title">
            حدد نوع التكاليف المالية التي تتحملها شركتكم بسبب عدم إفصاح المترشحين عن بياناتهم الشخصية:
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        <div className="data-category" data-field="financialCosts">
          {/* <div className="category-header">
            <DollarSign className="category-icon" size={24} />
            <h4>التكاليف المالية:</h4>
          </div> */}
          
          <div className={`checkbox-grid ${getErrorClass('financialCosts')}`}>
            {[
              'تكلفة إعادة التوظيف',
              'تكلفة إعادة التدريب',
              'تكلفة التحقق والمراجعة الاضافية',
              'تكلفة الأخطاء والمخاطر القانونية',
              'تكلفة الفرصة الضائعة لتوظيف موظف مناسب',
              'غير مهم'
            ].map(cost => (
              <label key={cost} className="checkbox-item cost-item">
                <input
                  type="checkbox"
                  checked={(data.financialCosts || []).includes(cost)}
                  onChange={() => handleMultiSelect('financialCosts', cost)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{cost}</span>
              </label>
            ))}
          </div>
          {renderError('financialCosts')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-costs">تكاليف أخرى (يرجى ذكرها):</label>
            <input
              id="other-costs"
              type="text"
              value={data.otherFinancialCosts || ''}
              onChange={(e) => handleOtherFieldChange('otherFinancialCosts', e.target.value)}
              placeholder="تكاليف مالية أخرى..."
              className={`other-input ${getErrorClass('otherFinancialCosts')}`}
            />
            {renderError('otherFinancialCosts')}
          </div>
        </div>
      </div>

      {/* Negative Effects Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">21</span>
          <label className="question-title">
            حدد الأثار السلبية التي تتحملها شركتكم بسبب عدم افصاح المترشحين عن بياناتهم الشخصية:
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        <div className="data-category" data-field="negativeEffects">
          {/* <div className="category-header">
            <TrendingDown className="category-icon" size={24} />
            <h4>الآثار السلبية:</h4>
          </div> */}
          
          <div className={`checkbox-grid ${getErrorClass('negativeEffects')}`}>
            {[
              'ضياع وقت الإدارة والموظفين',
              'تأخير المشاريع والخطط',
              'انخفاض معنويات الفريق',
              'تدهور جودة الخدمة/المنتج',
              'تضرر سمعة الشركة',
              'فقدان ثقة العملاء',
              'تأثير سلبي على الشركاء',
              'عدم استقرار بيئة العمل',
              'غير مهم'
            ].map(effect => (
              <label key={effect} className="checkbox-item effect-item">
                <input
                  type="checkbox"
                  checked={(data.negativeEffects || []).includes(effect)}
                  onChange={() => handleMultiSelect('negativeEffects', effect)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{effect}</span>
              </label>
            ))}
          </div>
          {renderError('negativeEffects')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-effects">أخرى (يرجى ذكرها):</label>
            <input
              id="other-effects"
              type="text"
              value={data.otherNegativeEffects || ''}
              onChange={(e) => handleOtherFieldChange('otherNegativeEffects', e.target.value)}
              placeholder="آثار سلبية أخرى..."
              className={`other-input ${getErrorClass('otherNegativeEffects')}`}
            />
            {renderError('otherNegativeEffects')}
          </div>
        </div>
      </div>

      {/* Annual Costs Estimate Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">22</span>
          <label className="question-title">
            حدد التقدير الإجمالي للتكاليف السنوية الناتجة عن عدم إفصاح المترشحين عن بياناتهم الشخصية؟
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <BarChart3 className="category-icon" size={24} />
            <h4>التقدير الإجمالي السنوي:</h4>
          </div> */}
          
          <div className={`radio-options-grid ${getErrorClass('annualCosts')}`}>
            {[
              { value: 'اقل من 500.000 ريال', range: '< 500K', level: 'منخفض' },
              { value: 'من 500.001 ريال إلى 1.000.000 ريال', range: '500K - 1M', level: 'متوسط' },
              { value: 'من 1.000.001 ريال إلى 1.500.000 ريال', range: '1M - 1.5M', level: 'مرتفع' },
              { value: 'من 1.500.001 ريال إلى 2.000.000 ريال', range: '1.5M - 2M', level: 'مرتفع جداً' },
              { value: 'أكثر من 2.000.000 ريال', range: '> 2M', level: 'استثنائي' },
              { value: 'يصعب تقديرها بدقة', range: 'غير محدد', level: 'غير واضح' }
            ].map(option => (
              <label key={option.value} className="radio-option cost-estimate-option">
                <input
                  type="radio"
                  name="annualCosts"
                  value={option.value}
                  checked={data.annualCosts === option.value}
                  onChange={(e) => onChange('annualCosts', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <div className="option-content">
                  <span className="radio-text">{option.value}</span>
                  <div className="cost-details">
                    <span className="cost-range">{option.range}</span>
                    <span className="cost-level">{option.level}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          {renderError('annualCosts')}
          
          {data.annualCosts === 'يصعب تقديرها بدقة' && (
            <div className="other-input-group">
              <label className="other-label" htmlFor="cost-estimation-reason">ولماذا؟</label>
              <textarea
                id="cost-estimation-reason"
                value={data.costEstimationReason || ''}
                onChange={(e) => handleOtherFieldChange('costEstimationReason', e.target.value)}
                placeholder="أسباب صعوبة التقدير..."
                className={`other-textarea ${getErrorClass('costEstimationReason')}`}
                rows="3"
              />
              {renderError('costEstimationReason')}
            </div>
          )}
        </div>
      </div>

      {/* Verification Cost Per Candidate Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">23</span>
          <label className="question-title">
            ما هو متوسط التكلفة التقديرية التي تتحملها شركتكم لعملية التحقق من البيانات الشخصية لكل مترشح للتعيين؟
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <DollarSign className="category-icon" size={24} />
            <h4>تكلفة التحقق لكل مترشح:</h4>
          </div> */}
          
          <div className={`radio-options-grid ${getErrorClass('verificationCostPerCandidate')}`}>
            {[
              'أقل من 250 ريال',
              '251 إلى 500 ريال',
              '501 إلى 1000 ريال',
              '1001 إلى 1500 ريال',
              '1501 إلى 2000 ريال',
              'أكثر من 2000 ريال',
              'لا يمكن تقدير هذه التكلفة بدقة'
            ].map(option => (
              <label key={option} className="radio-option verification-cost-option">
                <input
                  type="radio"
                  name="verificationCostPerCandidate"
                  value={option}
                  checked={data.verificationCostPerCandidate === option}
                  onChange={(e) => onChange('verificationCostPerCandidate', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <span className="radio-text">{option}</span>
              </label>
            ))}
          </div>
          {renderError('verificationCostPerCandidate')}
        </div>
      </div>

      {/* Current Procedures Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">24</span>
          <label className="question-title">
            حدد الإجراءات التي تطبقها حالياً شركتكم لضمان الإفصاح الكامل من المترشحين؟
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        <div className="data-category" data-field="currentProcedures">
          {/* <div className="category-header">
            <MessageCircle className="category-icon" size={24} />
            <h4>الإجراءات الحالية:</h4>
          </div> */}
          
          <div className={`checkbox-grid ${getErrorClass('currentProcedures')}`}>
            {[
              'تشديد متطلبات الإفصاح في الإعلانات',
              'استخدام نماذج تفصيلية للتقديم',
              'إجراء مقابلات متعددة ومتعمقة',
              'التحقق من المراجع بدقة',
              'الاستعانة بشركات تحقق متخصصة',
              'وضع بنود جزائية في عقود العمل',
              'فترات تجريبية مطولة',
              'غير مهم'
            ].map(procedure => (
              <label key={procedure} className="checkbox-item procedure-item">
                <input
                  type="checkbox"
                  checked={(data.currentProcedures || []).includes(procedure)}
                  onChange={() => handleMultiSelect('currentProcedures', procedure)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{procedure}</span>
              </label>
            ))}
          </div>
          {renderError('currentProcedures')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-procedures">أخرى (يرجى ذكرها):</label>
            <input
              id="other-procedures"
              type="text"
              value={data.otherCurrentProcedures || ''}
              onChange={(e) => handleOtherFieldChange('otherCurrentProcedures', e.target.value)}
              placeholder="إجراءات أخرى..."
              className={`other-input ${getErrorClass('otherCurrentProcedures')}`}
            />
            {renderError('otherCurrentProcedures')}
          </div>
        </div>
      </div>

      {/* Verification Time Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">25</span>
          <label className="question-title">
            ما هو متوسط الزمن الذي تستغرقه عملية التحقق من البيانات الشخصية للمتقدم الواحد؟
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <Clock className="category-icon" size={24} />
            <h4>مدة التحقق:</h4>
          </div> */}
          
          <div className={`radio-options-grid ${getErrorClass('verificationTime')}`}>
            {[
              { value: 'أقل من يوم عمل واحد', duration: '< 1 يوم', speed: 'سريع جداً' },
              { value: 'يوم إلى 2 يوم عمل', duration: '1-2 يوم', speed: 'سريع' },
              { value: '3 إلى 5 أيام عمل', duration: '3-5 أيام', speed: 'متوسط' },
              { value: 'أكثر من 5 أيام عمل', duration: '> 5 أيام', speed: 'بطيء' }
            ].map(option => (
              <label key={option.value} className="radio-option time-option">
                <input
                  type="radio"
                  name="verificationTime"
                  value={option.value}
                  checked={data.verificationTime === option.value}
                  onChange={(e) => onChange('verificationTime', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <div className="option-content">
                  <span className="radio-text">{option.value}</span>
                  <div className="time-details">
                    <span className="time-duration">{option.duration}</span>
                    <span className="time-speed">{option.speed}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          {renderError('verificationTime')}
        </div>
      </div>

      {/* Monthly Applicants Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">26</span>
          <label className="question-title">
            ما هو متوسط عدد المتقدمين للتعيين الذين تستقبلهم شركتكم شهرياً؟
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <Users className="category-icon" size={24} />
            <h4>عدد المتقدمين شهرياً:</h4>
          </div> */}
          
          <div className={`radio-options-grid ${getErrorClass('monthlyApplicants')}`}>
            {[
              'أقل من 5 متقدم',
              '6 إلى 10 متقدمين',
              '11 إلى 20 متقدمين',
              '21 إلى 30 متقدمين',
              'أكثر من 30 متقدمين'
            ].map(option => (
              <label key={option} className="radio-option applicants-option">
                <input
                  type="radio"
                  name="monthlyApplicants"
                  value={option}
                  checked={data.monthlyApplicants === option}
                  onChange={(e) => onChange('monthlyApplicants', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <span className="radio-text">{option}</span>
              </label>
            ))}
          </div>
          {renderError('monthlyApplicants')}
        </div>
      </div>

      {/* Monthly Interviews Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">27</span>
          <label className="question-title">
            ما هو متوسط عدد مقابلات التعيين التي تجريها شركتكم شهرياً؟
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <MessageCircle className="category-icon" size={24} />
            <h4>عدد المقابلات شهرياً:</h4>
          </div> */}
          
          <div className={`radio-options-grid ${getErrorClass('monthlyInterviews')}`}>
            {[
              'أقل من 5 مقابلات',
              '6 إلى 10 مقابلات',
              '11 إلى 20 مقابلة',
              '21 إلى 30 مقابلة',
              'أكثر من 30 مقابلة'
            ].map(option => (
              <label key={option} className="radio-option interviews-option">
                <input
                  type="radio"
                  name="monthlyInterviews"
                  value={option}
                  checked={data.monthlyInterviews === option}
                  onChange={(e) => onChange('monthlyInterviews', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <span className="radio-text">{option}</span>
              </label>
            ))}
          </div>
          {renderError('monthlyInterviews')}
        </div>
      </div>

      {/* Challenges Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">28</span>
          <label className="question-title">
            ما هي أبرز التحديات التي تواجه شركتكم في الحصول على الإفصاح الكامل والدقيق للبيانات الشخصية؟
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        <div className="data-category" data-field="disclosureChallenges">
          {/* <div className="category-header">
            <AlertTriangle className="category-icon" size={24} />
            <h4>التحديات المواجهة:</h4>
          </div> */}
          
          <div className={`checkbox-grid ${getErrorClass('disclosureChallenges')}`}>
            {[
              'عدم رغبة الأفراد في مشاركة بعض البيانات الحساسة',
              'تعقيد الإجراءات القانونية أو التنظيمية للحصول على بعض البيانات',
              'صعوبة التحقق من صحة بعض البيانات المقدمة',
              'ارتفاع تكلفة عمليات التحقق',
              'نقص الوعي لدى الأفراد بأهمية الإفصاح',
              'تعدد الجهات المعنية بالإفصاح بالبيانات الشخصية',
              'الخوف من سوء استخدام البيانات من قبل الشركات',
              'غير مهم'
            ].map(challenge => (
              <label key={challenge} className="checkbox-item challenge-item">
                <input
                  type="checkbox"
                  checked={(data.disclosureChallenges || []).includes(challenge)}
                  onChange={() => handleMultiSelect('disclosureChallenges', challenge)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{challenge}</span>
              </label>
            ))}
          </div>
          {renderError('disclosureChallenges')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-challenges">أخرى (يرجى ذكرها):</label>
            <input
              id="other-challenges"
              type="text"
              value={data.otherDisclosureChallenges || ''}
              onChange={(e) => handleOtherFieldChange('otherDisclosureChallenges', e.target.value)}
              placeholder="تحديات أخرى..."
              className={`other-input ${getErrorClass('otherDisclosureChallenges')}`}
            />
            {renderError('otherDisclosureChallenges')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostsProcedures;